import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { QuoteType, TradeModel, TradeOrderType } from "../models/trade/trade.model";
import { IComponent } from "./component.interface";
import { DrawExitOrderComponent } from "./draw-exit-order.component";
import { InformationPopupComponent } from "./information-popup.component";
import { LastPriceComponent } from "./last-price.component";
import { ProfitTargetPlotLine } from "./profit-target-plot-line.component";
import { StopLossPlotLine } from "./stop-loss-plot-line.component";
import { TradeComponent } from "./trade.component";

export class ExitOrderPlotLineComponent implements IComponent {
    public id = '';
    public mouseHover = false;
    public elementWidth: number | null = null;
    public elementHeight: number | null = null;

    public readonly EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS = 54;
    public readonly EXTRA_RIGHT = 85;
    public readonly EXTRA_PADDING_RIGHT = -20;

    private base: IChartContainer;
    private tradeComponent: TradeComponent;
    public profitTargetPlotLine: ProfitTargetPlotLine | null = null;
    public stoppLossPlotLine: StopLossPlotLine | null = null;
    public drawExitOrderComponent: DrawExitOrderComponent;
    public model: CurrentPositionViewModel;
    private color = new ChartStyleColorModel({ r: 0, g: 79, b: 208 });
    public lastPrice = 0;
    public isLong = false;
    public hide = false;
    private plotLine: any = null;
    private clonedPlotLine: any = null;
    private g: any = null;
    private bracketIcon: any = null;
    private fObj: any = null;
    private index = 1;
    public left = -1;
    public top = -1;
    private divElement: HTMLElement | null = null;
    private lastPriceDivElement: HTMLElement | null = null;

    constructor(base: IChartContainer, drawExitOrderComponent: DrawExitOrderComponent, model: CurrentPositionViewModel) {
        this.base = base;
        this.drawExitOrderComponent = drawExitOrderComponent;
        this.model = model;
        this.id = 'exit-order-' + this.model.getKey();
        this.lastPrice = base.tiChartSeries.lastPrice;
        this.tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;

        this.validateIsLongOrShort();
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.createSytle();
            this.setPosition();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.createSytle();
            this.setPosition();
        });
        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.lastPrice = lastPrice;
            this.createSytle();
            this.setPosition();
        });

        this.createSytle();
        this.setPosition();
        this.setElementWidthAndHeight();
        this.init();
    }

    private setElementWidthAndHeight() {
        const element = this.getExitOrderWrapperElement();
        element.setAttribute('style', 'opacity: 0 !important; visibility: hidden !important; position: absolute !important; width: auto !important');
        this.base.tiChartSeries?.element?.insertAdjacentElement('beforeend', element);
        const width = element.clientWidth;
        const height = element.clientHeight;
        this.elementWidth = Math.ceil(width + this.EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS);
        this.elementHeight = Math.ceil(height);
        element.remove();
    }

    public remove() {
        this.divElement?.remove();
        this.divElement = null;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        yAxis.removePlotLine(this.id);
        yAxis.removePlotLine(this.id + '-cloned');

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);

        this.removeProfitTarget();
        this.removeStopLoss();

        try {
            this.lastPriceDivElement?.remove();
            this.fObj?.destroy();
            this.g?.destroy();
            this.bracketIcon?.destroy();

            this.drawExitOrderComponent.selectedPlotLine = null;
        }
        catch { }
    }

    public createProfitTargetFromTradeOrder(model: TradeOrderDataModel) {
        this.removeProfitTarget();

        if (model.shares != this.model.shares) {
            this.base.chartService.requestUpdateChartTrade(model.id, model.price, this.model.shares);
        }

        this.profitTargetPlotLine = new ProfitTargetPlotLine(this.base, this, this.left, model);
        this.divElement?.querySelector('.profit-target-button')?.classList.add('hidden');
    }

    public createStopLossFromTradeOrder(model: TradeOrderDataModel) {
        this.removeStopLoss();

        if (model.shares != this.model.shares) {
            this.base.chartService.requestUpdateChartTrade(model.id, model.price, this.model.shares);
        }

        this.stoppLossPlotLine = new StopLossPlotLine(this.base, this, this.left, model);
        this.divElement?.querySelector('.stop-loss-button')?.classList.add('hidden');
    }

    public createTemporaryStopLoss(model: TradeOrderDataModel) {
        this.removeStopLoss();

        this.stoppLossPlotLine = new StopLossPlotLine(this.base, this, this.left, model);
        this.divElement?.querySelector('.stop-loss-button')?.classList.add('hidden');
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();

        const color = !this.hide ? this.color.toRGB() : 'transparent';
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke: ${color} !important;}`;
        styleTag += `.highcharts-plot-line.${className}-cloned {stroke: transparent !important; stroke-width: 18px !important;}`;

        styleTag += `
            .last-price-wrapper.exit-order {
                background-color: ${this.color.toRGB()} !important;
            } 
            .last-price-wrapper.exit-order .line {
                position: absolute;
                right: 0px;
                top: 50%;
                background-color: ${this.color.toRGB()} !important;
                height: 1px;
            }
            .last-price-wrapper.exit-order .last-price p {
                z-index: 1;
                position: relative;
            }
            .last-price-wrapper.exit-order .last-price:before {
                border-color: transparent ${this.color.toRGB()} transparent transparent !important;
            }
        `;
        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];

        this.createSytle();
        if (yAxis) {
            this.plotLine = yAxis.addPlotLine({
                value: this.model.averageCost,
                className: this.id,
                id: this.id,
                zIndex: this.index,
            });

            this.clonedPlotLine = yAxis.addPlotLine({
                value: this.model.averageCost,
                className: this.id + '-cloned',
                id: this.id + '-cloned',
                zIndex: this.index + 3,
                events: {
                    mouseover: () => {
                        clearTimeout(this.mouseHoverTimer);
                        this.mouseHoverTimer = setTimeout(() => {
                            const element = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);

                            this.toFront();

                            this.mouseHover = true;
                            this.base.tiChartSeries.preventDragging = true;
                            element?.addClass('hover');
                        }, 150);
                    },
                    mouseout: () => {
                        this.mouseHover = false;
                        clearTimeout(this.mouseHoverTimer);
                        const element = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);

                        this.toNormal();
                        element?.removeClass('hover');
                    },
                }
            });

            this.createIcon();

            setTimeout(() => {
                this.setPosition();
            }, 500);
        }
    }

    public toFront() {
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 17 });
        }
    }

    public toNormal() {
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 16 });
        }
    }

    public getColor(positive: boolean): ChartStyleColorModel {
        if (positive) {
            return new ChartStyleColorModel({
                r: 38,
                g: 142,
                b: 55
            });
        } else {
            return new ChartStyleColorModel({
                r: 240,
                g: 54,
                b: 68
            });
        }
    }

    private validateIsLongOrShort() {
        if (this.model.shares >= 0) {
            this.isLong = true;
        }
    }

    private setPosition() {
        const container = this.base.chartWrapper;
        if (!container) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const shouldDisplayExitOrder = this.tradeComponent && this.lastPrice;
        if (shouldDisplayExitOrder) {
            const profit = this.model.getTotalProfit(this.lastPrice);
            const profitString = FormatHelpers.numberToStringThousandSeparators(Math.abs(profit), true, 0);

            this.top = yAxis.toPixels(this.model.averageCost, true);
            this.top += 45;

            this.hide = false;
            if (this.model.averageCost > yAxis.max || this.model.averageCost < yAxis.min) {
                this.hide = true;
            }

            container.querySelectorAll<HTMLElement>(`.exit-order-wrapper.${this.id}`).forEach((el: HTMLElement) => {
                if (!el.classList.contains('cloned')) {
                    if (this.hide) {
                        el.style.display = 'none';
                    } else {
                        el.style.display = '';
                    }

                    el.findFirst('.center')?.setInnerHtml(`
                    <p class="value ${profit >= 0 || profitString == '0' ? '' : 'neg'}">
                        ${profit >= 0 || profitString == '0' ? '' : '-'}$${profitString}
                    </p>`);
                }
            });

            try {
                this.fObj?.element?.setAttribute('y', this.top);
            } catch { }
        }

        this.createLastPrice();
    }

    private createLastPrice() {

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const currentYValue = yAxis.toPixels(this.model.averageCost) ?? 0;
        const myHeight = 7;

        const valueTop = (yAxis.toPixels(yAxis.max) ?? 0) - 16;
        const valueBottom = (yAxis.toPixels(yAxis.min) ?? 0) - 16;

        let top = currentYValue - myHeight;
        let hide = false;

        if (this.model.averageCost > yAxis.max || valueTop > top) {
            top = valueTop;
            hide = true;
        } else if (this.model.averageCost < yAxis.min || top > valueBottom) {
            top = valueBottom;
            hide = true;
        }

        const priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.model.averageCost, true);

        if (!this.lastPriceDivElement) {
            this.lastPriceDivElement = this.base.chartWrapper.querySelector<HTMLElement>('.last-price-wrapper.exit-order');
        }

        if (this.lastPriceDivElement) {
            this.lastPriceDivElement.style.top = top + 'px';

            const pTag = this.lastPriceDivElement.querySelector('p');
            if (pTag) {
                pTag.innerHTML = priceToString;
            }
        } else {
            this.lastPriceDivElement = document.createElement('div');
            this.lastPriceDivElement.className = 'last-price-wrapper exit-order';
            this.lastPriceDivElement.style.top = top + 'px';
            this.lastPriceDivElement.innerHTML = `
                <div class="last-price">
                    <p>${priceToString}</p>
                    <div class="line"></div>
                </div>
            `;

            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.lastPriceDivElement);
            }
        }

        if (hide) {
            this.lastPriceDivElement?.classList.add('hidden');
        } else {
            this.lastPriceDivElement?.classList.remove('hidden');
        }
    }

    private getExitOrderWrapperElement(): HTMLElement {
        const profit = this.model.getTotalProfit(this.lastPrice);
        const profitString = FormatHelpers.numberToStringThousandSeparators(Math.abs(profit), true, 0);
        const divElement = document.createElement('div');
        divElement.className = `exit-order-wrapper ${this.id}`;
        divElement.title = this.model.temporary ? '' : `Select "X" to Close (flatten) this Position`;
        divElement.setAttribute('style', `${this.hide ? `display: none;` : ``}`);
        divElement.innerHTML = `
            <div class="profit-target-button ${this.model.temporary ? 'hidden' : ''}" title="Click and Drag to add Profit Target">
                <svg style="cursor:pointer;color:${this.color.toRGB()};">
                    <use href="${this.base.basePath}assetfiles/img/profit-target-v2.svg#profit-target-v2"></use>
                </svg>
            </div>
            <div class="stop-loss-button ${this.model.temporary ? 'hidden' : ''}" title="Click and Drag to add Stop Loss">
                <svg style="cursor:pointer;color:${this.color.toRGB()};">
                    <use href="${this.base.basePath}assetfiles/img/stop-loss-v2.svg#stop-loss-v2"></use>
                </svg>
            </div>
            <div class="left item">
                <p class="value">${this.isLong ? '' : '-'}${Math.abs(this.model.shares)}</p>
            </div>
            <div class="center item" style="border: 1px solid ${this.color.toRGB()}; border-left: none; border-right: none;">
                <p class="value ${profit >= 0 || profitString == '0' ? '' : 'neg'}">
                    ${profit >= 0 || profitString == '0' ? '' : '-'}$${profitString}
                </p>
            </div>
            <div class="right item" style="border: 1px solid ${this.color.toRGB()}; border-left: none; ${this.model.temporary ? 'cursor: default !important;' : ''}">
                <svg style="color:${this.color.toRGB()};" class="exit-order ${this.model.temporary ? 'hidden' : ''}" >
                    <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                </svg>
            </div>
        `;

        return divElement;
    }

    private clossAll() {
        const tradeIsBuy = !this.isLong;
        const tradeModel = new TradeModel({
            price: this.lastPrice,
            isBuy: tradeIsBuy,
            orderType: TradeOrderType.Market,
            quoteType: QuoteType.Shares,
            account: this.model.accountName,
            quoteQuantity: Math.abs(this.model.shares)
        });

        this.mouseHover = false;
        this.base.tiChartSeries.preventDragging = false;
        if (!this.model.temporary) {
            this.base.chartService.requestManualTrade(tradeModel);
        } else {
            (this.base.chartService as any)?.clearTemporaryPosition();
        }

        this.closeProfitTarget();
        this.closeStopLoss();
        this.remove();
    }

    private mouseHoverTimer: any;
    private createIcon() {
        const container = this.base.chartWrapper;
        if (!container) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const shouldDisplayExitOrder = this.tradeComponent && this.lastPrice;
        if (shouldDisplayExitOrder) {

            try {
                if (this.g) {
                    this.g?.destroy();
                }
            } catch { }

            this.top = yAxis.toPixels(this.model.averageCost, true);
            this.top += 45;

            this.left = this.base.tiChartSeries.element.clientWidth - this.elementWidth - (this.EXTRA_RIGHT * 1.20);

            if (isNaN(this.top) || isNaN(this.left)) return;

            const renderer = this.base.tiChartSeries.highStockChart.renderer;
            this.hide = false;
            if (this.model.averageCost > yAxis.max || this.model.averageCost < yAxis.min) {
                this.hide = true;
            }

            this.divElement = this.getExitOrderWrapperElement();
            this.g = renderer.g(this.id).attr({
                zIndex: 16
            });

            const foreignObjectElement = renderer.createElement('foreignObject');
            foreignObjectElement.element.appendChild(this.divElement);
            foreignObjectElement.element.setAttribute('width', this.elementWidth);
            foreignObjectElement.element.setAttribute('height', 25 + '');
            foreignObjectElement.element.setAttribute('x', this.left);
            foreignObjectElement.element.setAttribute('y', this.top);
            foreignObjectElement.add(this.g);

            this.fObj = foreignObjectElement;
            this.g.add();

            setTimeout(() => {
                const originalElement = container?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
                originalElement?.ifExistThen((b: HTMLElement) => {
                    if (b.containClass('inited')) {
                        return;
                    }
                    b.addClass('inited');
                    b.addEventListener('mouseenter', () => {
                        this.mouseHover = true;
                        this.base.tiChartSeries.preventDragging = true;
                        clearTimeout(this.mouseHoverTimer);

                        this.toFront();

                        b.addClass('hover');
                    });
                    b.addEventListener('click', () => {
                        this.mouseHover = true;
                        this.base.tiChartSeries.preventDragging = true;
                        clearTimeout(this.mouseHoverTimer);

                        this.toFront();

                        b.addClass('hover');
                    });
                    b.addEventListener('mouseleave', () => {
                        b.removeClass('hover');

                        this.toNormal();

                        this.mouseHoverTimer = setTimeout(() => {
                            this.mouseHover = false;
                            this.base.tiChartSeries.preventDragging = false;
                        }, 500);
                    });
                    b.findFirst('.right')?.ifExistThen((r: HTMLElement) => {
                        const remove = () => {
                            if (this.model.temporary) return;
                            
                            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                            popup?.show(`Exit Order`, this.getExitOrderMessage(), () => {
                                this.clossAll();
                            });
                        }
                        if (r.containClass('inited')) {
                            return;
                        }
                        r.classList.add('inited');
                        r.addEventListener('mouseenter', () => {
                            r.classList.add('hover');
                        });
                        r.addEventListener('mouseleave', () => {
                            r.classList.remove('hover');
                        });
                        r.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            remove();
                        });

                        const s = r.querySelector('svg')
                        if (!s || s.classList.contains('inited')) {
                            return;
                        }
                        s.classList.add('inited');
                        s.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            remove();
                        });
                    });
                    b.findFirst('.profit-target-button')?.ifExistThen((p: HTMLElement) => {
                        if (p.containClass('inited') || this.model.temporary) {
                            return;
                        }

                        p.addClass('inited');

                        if (this.drawExitOrderComponent.requestingProfitTarget) {
                            p?.addClass('hidden');
                        }

                        p.addEventListener('mousedown', () => {
                            this.base.tiChartSeries.preventDragging = true;
                        });
                        p.addEventListener('mouseup', () => {
                            setTimeout(() => {
                                this.base.tiChartSeries.preventDragging = false;
                            }, 500);
                        });
                        p.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!this.profitTargetPlotLine) {
                                const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
                                if (!yAxis) {
                                    return;
                                }

                                const price = this.isLong ? this.getPrice_LongProfitTarget() : this.getPrice_ShortProfitTarget();
                                const tradeModel = this.isLong ? this.generateOrder_LongProfitTarget(price) : this.generateOrder_ShortProfitTarget(price);
                                this.base.chartService.requestManualTrade(tradeModel);

                                this.drawExitOrderComponent.requestingProfitTarget = true;

                                // this.profitTargetPlotLine = new ProfitTargetPlotLine(this.base, this, this.left, new TradeOrderDataModel({
                                //     price: price,
                                // }));
                                p?.addClass('hidden');
                            }
                        });
                    });
                    b.findFirst('.stop-loss-button')?.ifExistThen((p: HTMLElement) => {
                        if (p.containClass('inited') || this.model.temporary) {
                            return;
                        }

                        p.addClass('inited');

                        if (this.drawExitOrderComponent.requestingStopLoss) {
                            p?.addClass('hidden');
                        }

                        p.addEventListener('mousedown', () => {
                            this.base.tiChartSeries.preventDragging = true;
                        });
                        p.addEventListener('mouseup', () => {
                            setTimeout(() => {
                                this.base.tiChartSeries.preventDragging = false;
                            }, 500);
                        });
                        p.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!this.stoppLossPlotLine) {
                                const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
                                if (!yAxis) {
                                    return;
                                }

                                const price = this.isLong ? this.getPrice_LongStopLoss() : this.getPrice_ShortStopLoss();
                                const tradeModel = this.isLong ? this.generateOrder_LongStopLoss(price) : this.generateOrder_ShortStopLoss(price);

                                this.base.chartService.requestManualTrade(tradeModel);

                                this.drawExitOrderComponent.requestingStopLoss = true;
                                // this.stoppLossPlotLine = new StopLossPlotLine(this.base, this, this.left, new TradeOrderDataModel({
                                //     price: price,
                                // }));
                                p?.addClass('hidden');
                            }
                        });
                    });
                });
            }, 300);
        }
    }

    private getPrice_LongProfitTarget(): number {
        // if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
        //     return this.lastPrice * 1.15;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
        //     return this.lastPrice * 1.10;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
        //     return this.lastPrice * 1.05;
        // }

        // return this.lastPrice * 1.01;

        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let entryPrice = this.model.averageCost;
        let lastPrice = this.lastPrice;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        let lastPriceYAxisPixels = 0;
        if (lastPriceComp) {
            lastPriceYAxisPixels = lastPriceComp.getElementTop();
        }

        const entryOrderYAxisPixels = this.top;
        const profitTargetShoudlBeAboveEntryOrder = entryPrice > lastPrice;
        let top = profitTargetShoudlBeAboveEntryOrder ? entryOrderYAxisPixels : lastPriceYAxisPixels;
    
        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top -= 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top -= 30;
        }

        let price = yAxis.toValue(top);
        return price;
    }

    private getPrice_ShortProfitTarget(): number {
        // if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
        //     return this.lastPrice * 0.75;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
        //     return this.lastPrice * 0.9;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
        //     return this.lastPrice * 0.95;
        // }

        // return this.lastPrice * 0.99;
        
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let entryPrice = this.model.averageCost;
        let lastPrice = this.lastPrice;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        let lastPriceYAxisPixels = 0;
        if (lastPriceComp) {
            lastPriceYAxisPixels = lastPriceComp.getElementTop();
        }

        const entryOrderYAxisPixels = this.top;
        const profitTargetShoudlBeBelowEntryOrder = entryPrice < lastPrice;
        let top = profitTargetShoudlBeBelowEntryOrder ? entryOrderYAxisPixels : lastPriceYAxisPixels;

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top += 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top += 30;
        }

        let price = yAxis.toValue(top);
        return price;
    }

    private getPrice_LongStopLoss(): number {
        // if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
        //     return this.lastPrice * 0.925;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
        //     return this.lastPrice * 0.95;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
        //     return this.lastPrice * 0.975;
        // }

        // return this.lastPrice * 0.99;
                                
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let top = this.top;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        // if (lastPriceComp && lastPriceComp.getElementTop() > top) {
        if (lastPriceComp) {
            top = lastPriceComp.getElementTop();
        }

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top += 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top += 30;
        }


        let price = yAxis.toValue(top);
        return price;
    }

    private getPrice_ShortStopLoss(): number {
        // if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
        //     return this.lastPrice * 1.075;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
        //     return this.lastPrice * 1.05;
        // } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
        //     return this.lastPrice * 1.025;
        // }

        // return this.lastPrice * 1.01;

        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let top = this.top;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        // if (lastPriceComp && lastPriceComp.getElementTop() < top) {
        if (lastPriceComp) {
            top = lastPriceComp.getElementTop();
        }

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top -= 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top -= 30;
        }

        let price = yAxis.toValue(top);
        return price;
    }

    private getOCAGroup(): string {
        return `${this.model.symbol}-OCA-${DateHelpers.toTimeT(new Date())}`;
    }

    private generateOrder_LongProfitTarget(price: number): TradeModel {
        //Long - Profit Target is Sell Limit Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.model.accountName,
            quoteQuantity: Math.abs(this.model.shares)
        });

        return tradeModel;
    }

    private generateOrder_LongStopLoss(price: number): TradeModel {
        //Long - Stop Loss is Sell Stop Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.model.accountName,
            quoteQuantity: Math.abs(this.model.shares)
        });

        return tradeModel;
    }

    private generateOrder_ShortProfitTarget(price: number): TradeModel {
        //Short - Profit target is Buy Limit Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.model.accountName,
            quoteQuantity: Math.abs(this.model.shares)
        });

        return tradeModel;
    }

    private generateOrder_ShortStopLoss(price: number): TradeModel {
        //Short - Stop loss is Buy Stop Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.model.accountName,
            quoteQuantity: Math.abs(this.model.shares)
        });

        return tradeModel;
    }

    // // this.onMouseMove();
    // // this.onMouseDown();

    // private addProfitTargetPlotLine(defaultTop: number) {
    //     this.defaultAddPlotLineTopValue = defaultTop;

    //     const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
    //     if (!yAxis) {
    //         return;
    //     }

    //     yAxis.addPlotLine({
    //         value: 0,
    //         id: this.PROFIT_TARGET_PRE_ADDED_KEY,
    //         dashStyle: 'Dash',
    //         className: 'sell-plot-line',
    //         zIndex: 10
    //     });
    // }

    // private addStopLossPlotLine(defaultTop: number) {
    //     this.defaultAddPlotLineTopValue = defaultTop;
    // }

    public addBracketIcon() {
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        try {
            this.bracketIcon?.destroy();
            this.bracketIcon = null;
        } catch { }

        if (this.hide || !this.plotLine) return;

        const color = this.color;
        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        const width = this.elementWidth;
        const currentY = yAxis.toPixels(this.model.averageCost, true) + 58;
        const left = this.left + width + 15;

        this.bracketIcon = renderer.circle(left, currentY + 1.5, 3)
            .attr({
                'fill': `${color.toRGB()}`,
                'stroke': `${color.toRGB()}`,
                'stroke-width': 1.5,
                'zIndex': 12
            })
            .add();
    }

    public closeProfitTarget() {
        if (this.profitTargetPlotLine?.tradeId) {
            this.base.chartService.requestRemoveChartTrade(this.profitTargetPlotLine?.tradeId);
            this.mouseHover = false;
            this.base.tiChartSeries.preventDragging = false;
        }

        const ocaGroup = this.profitTargetPlotLine?.model.ocaGroup ?? '';
        ocaGroup && this.drawExitOrderComponent.removeOcaGroups.push(ocaGroup);

        this.removeProfitTarget();
    }

    private removeProfitTarget() {
        this.profitTargetPlotLine?.remove();
        this.profitTargetPlotLine = null;
        const originalElement = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
        originalElement?.ifExistThen((b: HTMLElement) => {
            b.findFirst('.profit-target-button')?.ifExistThen((p: HTMLElement) => {
                p?.removeClass('hidden');
            });
        });
    }

    public closeStopLoss() {
        if (this.stoppLossPlotLine?.tradeId) {
            this.base.chartService.requestRemoveChartTrade(this.stoppLossPlotLine?.tradeId);
            this.mouseHover = false;
            this.base.tiChartSeries.preventDragging = false;
        }

        const ocaGroup = this.stoppLossPlotLine?.model.ocaGroup ?? '';
        ocaGroup && this.drawExitOrderComponent.removeOcaGroups.push(ocaGroup);

        this.removeStopLoss();
    }

    private removeStopLoss() {
        this.stoppLossPlotLine?.remove();
        this.stoppLossPlotLine = null;
        const originalElement = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
        originalElement?.ifExistThen((b: HTMLElement) => {
            b.findFirst('.stop-loss-button')?.ifExistThen((p: HTMLElement) => {
                p?.removeClass('hidden');
            });
        });
    }

    public getExitOrderMessage(): string {
        let html = ``;
        if (this.isLong) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(this.model.shares)} Shares</span> of <span class="b">${this.model.symbol}</span> at <span class="b">Market,</span><br/>to Close the Position in <span class="b">${this.model.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(this.model.shares)} Shares</span> of <span class="b">${this.model.symbol}</span> at <span class="b">Market,</span><br/>to Close the Position in <span class="b">${this.model.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }
}